import React from "react";
import classNames from "classnames";

import {MuiIcon} from "../icons/MuiIcon";

import "./TaskStatusMarker.css";

export type TaskStatusMarkerVariant = 'overdue' | 'corrective' | 'schedule_ended';

export interface TaskStatusMarkerProps {
    variant: TaskStatusMarkerVariant;
    count?: number;
    /** Number of distinct attention reasons on the task. When > 1 the marker
     *  renders a filled circle with this number instead of the variant icon -
     *  `variant` then only picks the circle's colour (the most severe reason). */
    problemCount?: number;
}

const VARIANT_ICON: Record<TaskStatusMarkerVariant, any> = {
    overdue: 'schedule',
    corrective: 'warning',
    schedule_ended: 'event_busy',
};

// Small row-level indicator shown next to a task's name: a clock for tasks with
// at least one overdue (still-closeable) occurrence, a warning triangle for
// corrective tasks, a "no more occurrences" calendar for tasks whose schedule
// has ended. When a task has been overdue more than once, a small unread-style
// count badge sits on top of the clock.
//
// A row only ever renders one of these at a time - when a task matches more
// than one state, TaskLineTitleCell passes `problemCount` and the marker becomes
// a single count circle coloured by the most severe reason; which reasons they
// are is left to the hover card (no stacked icons, no second number on the row).
export const TaskStatusMarker = ({variant, count, problemCount}: TaskStatusMarkerProps) => {
    if (problemCount && problemCount > 1) {
        return (
            <span className={classNames("TaskStatusMarker", "TaskStatusMarker--multi", `TaskStatusMarker--${variant}`)}>
                <span className="TaskStatusMarker__count label-small">{problemCount}</span>
            </span>
        );
    }

    const showBadge = variant === 'overdue' && !!count && count > 1;

    return (
        <span className={classNames("TaskStatusMarker", `TaskStatusMarker--${variant}`)}>
            <MuiIcon
                code={VARIANT_ICON[variant]}
                size={16}
                className="TaskStatusMarker__icon"
            />
            {showBadge && (
                <span className="TaskStatusMarker__badge">{count}</span>
            )}
        </span>
    );
};

export default TaskStatusMarker;
